
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

const PLANS = [
  {
    name: "Free",
    price: "0€",
    description: "Basic features for personal use",
    features: [
      { name: "50 tokens per month", included: true },
      { name: "1 email account", included: true },
      { name: "3 assistants", included: true },
      { name: "Answer up to 3 emails simultaneously", included: true },
      { name: "Maia signature in outgoing emails", included: true },
      { name: "Efficient model only", included: true },
    ],
    priceId: null, // Free plan doesn't have a price ID
    isPopular: false,
  },
  {
    name: "Pro",
    price: "5€",
    description: "Advanced features for professionals",
    features: [
      { name: "200 tokens per month", included: true },
      { name: "3 email accounts", included: true },
      { name: "5 assistants", included: true },
      { name: "Answer up to 5 emails simultaneously", included: true },
      { name: "No Maia signature", included: true },
      { name: "Efficient and Extended models", included: true },
    ],
    priceId: "price_1QUR9yFGS9T5vOCc1uGVaUq8",
    isPopular: true,
  },
  {
    name: "Premium",
    price: "10€",
    description: "Ultimate features for power users",
    features: [
      { name: "500 tokens per month", included: true },
      { name: "Unlimited email accounts", included: true },
      { name: "Unlimited assistants", included: true },
      { name: "Unlimited simultaneous email answers", included: true },
      { name: "No Maia signature", included: true },
      { name: "Efficient and Extended models", included: true },
    ],
    priceId: "price_1QSzETFGS9T5vOCcE4GqHwwc",
    isPopular: false,
  },
];

export const SubscriptionPlans = () => {
  const [isLoading, setIsLoading] = useState<{[key: string]: boolean}>({});
  const { toast } = useToast();

  const handleSubscribe = async (planName: string, priceId: string | null) => {
    if (!priceId) {
      toast({
        title: "Free Plan",
        description: "You are already on the free plan",
      });
      return;
    }

    try {
      setIsLoading(prev => ({ ...prev, [planName]: true }));
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to subscribe",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL received');

      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast({
        title: "Error",
        description: "Failed to initiate subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(prev => ({ ...prev, [planName]: false }));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold">Subscription Plans</h3>
        <p className="text-muted-foreground">Choose the plan that works for your needs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((plan) => (
          <Card key={plan.name} className={`p-6 flex flex-col ${plan.isPopular ? 'border-primary' : ''}`}>
            {plan.isPopular && (
              <div className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-full w-fit mb-4">
                Most Popular
              </div>
            )}
            <h4 className="text-xl font-bold">{plan.name}</h4>
            <div className="mt-2">
              <span className="text-3xl font-extrabold">{plan.price}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-muted-foreground mt-2">{plan.description}</p>
            
            <div className="mt-6 space-y-3 flex-1">
              {plan.features.map((feature) => (
                <div key={feature.name} className="flex items-center">
                  {feature.included ? (
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XIcon className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-sm">{feature.name}</span>
                </div>
              ))}
            </div>
            
            <Button 
              className="mt-6 w-full" 
              variant={plan.isPopular ? "default" : "outline"}
              disabled={isLoading[plan.name]}
              onClick={() => handleSubscribe(plan.name, plan.priceId)}
            >
              {isLoading[plan.name] ? "Loading..." : plan.priceId ? "Subscribe" : "Current Plan"}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
};
